import React from 'react';
import Icon from '../../../components/AppIcon';

const BlogPostCard = ({ post, onClick, getCategoryColor }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateReadingTime = (content) => {
    const wordsPerMinute = 200;
    
    // Parse HTML and extract plain text
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content || '';
    const plainText = tempDiv?.textContent || tempDiv?.innerText || '';
    
    // Clean up whitespace and count words
    const cleanedText = plainText?.replace(/\s+/g, ' ')?.trim();
    const wordCount = cleanedText?.split(/\s+/)?.filter(word => word?.length > 0)?.length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    
    return `${minutes} dk okuma`;
  };

  const truncateContent = (content, maxLength = 560) => {
    // Create a temporary div to parse HTML and extract text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content || '';
    const plainText = tempDiv?.textContent || tempDiv?.innerText || '';

    // Remove extra whitespace and newlines
    const cleanedText = plainText?.replace(/\s+/g, ' ')?.trim();

    if (cleanedText?.length <= maxLength) return cleanedText;
    return cleanedText?.substring(0, maxLength) + '...';
  };

  return (
    <article
      onClick={onClick}
      className="bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer group overflow-hidden border border-gray-100 transform hover:-translate-y-1"
    >
      <div className="md:flex">
        {/* Enhanced Cover Image */}
        <div className="md:w-2/5 relative overflow-hidden bg-gray-100">
          {post?.imageUrl ? (
            <div className="relative h-64 md:h-full md:min-h-[400px]">
              <img
                src={post?.imageUrl}
                alt={post?.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          ) : (
            <div className="h-64 md:h-full bg-gradient-to-br from-primary/20 via-primary/10 to-primary/5 flex items-center justify-center relative">
              <Icon name="FileText" className="w-20 h-20 text-primary/30" />
              <div className="absolute inset-0 bg-gradient-to-br from-transparent to-primary/10"></div>
            </div>
          )}
          
          {/* Category Badge Overlay */}
          {post?.category && (
            <div className="absolute top-4 left-4">
              <span
                className={`inline-flex items-center px-4 py-2 rounded-xl text-xs font-bold border-2 backdrop-blur-sm shadow-lg ${
                  getCategoryColor(post?.category?.color)
                }`}
              >
                {post?.category?.name}
              </span>
            </div>
          )}
        </div>

        {/* Enhanced Content */}
        <div className="md:w-3/5 p-8">
          {/* Meta Information */}
          <div className="flex items-center text-sm text-gray-500 mb-4 flex-wrap gap-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center mr-2">
                <Icon name="Calendar" className="w-4 h-4 text-blue-600" />
              </div>
              <span className="font-medium">{formatDate(post?.publishedAt || post?.createdAt)}</span>
            </div>
            <div className="flex items-center">
              <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center mr-2">
                <Icon name="Clock" className="w-4 h-4 text-green-600" />
              </div>
              <span className="font-medium">{calculateReadingTime(post?.content)}</span>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors line-clamp-2 leading-tight">
            {post?.title}
          </h2>

          {/* Excerpt */}
          <p className="text-gray-600 mb-6 leading-relaxed">
            {truncateContent(post?.content)}
          </p>

          {/* Footer: Author & Read More */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-100">
            {/* Author */}
            {post?.author && (
              <div className="flex items-center">
                {post?.author?.avatarUrl ? (
                  <img
                    src={post?.author?.avatarUrl}
                    alt={post?.author?.fullName}
                    className="w-10 h-10 rounded-full mr-3 border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center mr-3 border-2 border-primary/20">
                    <Icon name="User" className="w-5 h-5 text-primary" />
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {post?.author?.fullName}
                  </p>
                  <p className="text-xs text-gray-500">Yazar</p>
                </div>
              </div>
            )}

            {/* Read More Button */}
            <div className="flex items-center text-primary font-bold group-hover:translate-x-2 transition-transform duration-300">
              <span className="mr-2">Devamını Oku</span>
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                <Icon name="ArrowRight" className="w-4 h-4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export default BlogPostCard;